import { Channel, ChannelTypeEnum, Identity, Maybe } from '@vendorflow/types'
import { UseQueryOptions } from 'types/ReactQuery.type'
import { QueryFunctionContext, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import {
  addMembersToChannelUsingPOST,
  fetchChannelsForIdentityUsingGET,
  fetchChannelsForOrgUsingGET,
  fetchChannelUsingGET,
  fetchMessageForChannelUsingGET,
  fetchMessagesForChannelUsingGET,
  MIN_SEARCH_LENGTH,
} from 'apis/Channel.api'
import { keyBy } from 'lodash'
import { MessageWithContext } from 'types/Messages'
import { useMyIdentityId, useMyOrgId } from 'queries/Auth.queries'
import { CHATBOT_IDENTITY_ID } from '@vendorflow/common/lib/constants/Workspaces.constants'
import { useMemo } from 'react'
import { useDevAutomationCommandsEnabled } from 'features/feature-flags/FeatureFlag.hooks'
import { LONG_CACHE_TIME, LONG_STALE_TIME } from 'pages/WorkspacePage/Workspace.constants'
import { useInvalidateChannelsQueries } from 'queries/CacheInvalidation.queries'
import { extractMessageIdFromCompoundId } from 'services/Azimuth.service'
import { MessagesForChannelPageResult } from 'features/channels/Channels.types'
import ms from 'ms'

/**
 * List of Channels for Bob or Envy
 */
export function channelsQueryKey(
  type: QueryCacheEnum.IDENTITIES | QueryCacheEnum.ORGANIZATIONS,
  id: Maybe<string>,
  org?: Maybe<string> // only included when type is IDENTITIES
):
  | [QueryCacheEnum.ORGANIZATIONS, Maybe<string>, QueryCacheEnum.CHANNELS]
  | [QueryCacheEnum.IDENTITIES, Maybe<string>, QueryCacheEnum.ORGANIZATIONS, Maybe<string>, QueryCacheEnum.CHANNELS] {
  return type === QueryCacheEnum.ORGANIZATIONS
    ? [QueryCacheEnum.ORGANIZATIONS, id, QueryCacheEnum.CHANNELS]
    : [QueryCacheEnum.IDENTITIES, id, QueryCacheEnum.ORGANIZATIONS, org, QueryCacheEnum.CHANNELS]
}

export const CHANNELS_STALE_TIME = ms('5m')

export async function channelsQueryFn({ queryKey }: { queryKey: ReturnType<typeof channelsQueryKey> }) {
  let channels: Channel[] = []
  const type = queryKey[0]
  const id = queryKey[1]!
  let envy = id
  if (type === QueryCacheEnum.IDENTITIES) {
    envy = queryKey[3]!
    channels = await fetchChannelsForIdentityUsingGET(id, envy)
  } else {
    channels = await fetchChannelsForOrgUsingGET(id)
  }

  return channels
}

export function useChannels(
  id: Maybe<string>, // an identityId or orgId depending on the provided type
  type: QueryCacheEnum.IDENTITIES | QueryCacheEnum.ORGANIZATIONS,
  options?: UseQueryOptions
) {
  const enabled = options?.enabled !== undefined ? options.enabled : true
  const envy = useMyOrgId()

  return useQuery<Channel[], Error, Channel[], ReturnType<typeof channelsQueryKey>>(
    channelsQueryKey(type, id, envy),
    channelsQueryFn,
    {
      enabled: !!id && !!envy && enabled,
      staleTime: CHANNELS_STALE_TIME,
    }
  )
}

/**
 * Single Channel
 */
export function channelQueryKey(channelId: Maybe<string>): [QueryCacheEnum.CHANNELS, Maybe<string>] {
  return [QueryCacheEnum.CHANNELS, channelId]
}

export async function channelQueryFn({ queryKey }: { queryKey: ReturnType<typeof channelQueryKey> }) {
  const channelId = queryKey[1]!
  return fetchChannelUsingGET(channelId, true, true)
}

type UseChannelData = { channel: Channel; members: Identity[]; alice: Identity | null } | undefined

export function useChannel(channelId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<UseChannelData, Error, UseChannelData, ReturnType<typeof channelQueryKey>>(
    channelQueryKey(channelId),
    channelQueryFn,
    {
      enabled: !!channelId && enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export function useElizaEnabled(channelId: Maybe<string>) {
  const DEV_AUTOMATION_COMMANDS_ENABLED = useDevAutomationCommandsEnabled()
  const useChannelData = useChannel(channelId)
  const { channel, members } = useChannelData.data || { channel: undefined, members: [] }
  const membersById = useMemo(() => keyBy(members, 'id'), [useChannelData.dataUpdatedAt])
  return (
    channel?.channelType === ChannelTypeEnum.CAPTIVE_THREAD &&
    !!membersById[CHATBOT_IDENTITY_ID] &&
    DEV_AUTOMATION_COMMANDS_ENABLED
  )
}

/**
 * Single Message for Channel
 */
export function messageForChannelQueryKey(
  channelId: Maybe<string>,
  messageId: Maybe<string>
): [QueryCacheEnum.CHANNELS, Maybe<string>, QueryCacheEnum.MESSAGES, Maybe<string>] {
  return [QueryCacheEnum.CHANNELS, channelId, QueryCacheEnum.MESSAGES, messageId]
}

export function messageForChannelQueryFn({ queryKey }: { queryKey: ReturnType<typeof messageForChannelQueryKey> }) {
  const channel = queryKey[1]
  const message = queryKey[3]
  return fetchMessageForChannelUsingGET(channel!, message!)
}

export function useMessageForChannel(channelId: Maybe<string>, messageId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true
  const mId = useMemo(() => extractMessageIdFromCompoundId(messageId), [messageId])

  return useQuery<MessageWithContext, Error, MessageWithContext, ReturnType<typeof messageForChannelQueryKey>>(
    messageForChannelQueryKey(channelId, mId),
    messageForChannelQueryFn,
    {
      enabled: !!channelId && !!mId && enabled,
      staleTime: LONG_STALE_TIME,
      cacheTime: LONG_CACHE_TIME,
    }
  )
}

/**
 * Paginated Messages For Channel
 */
export function messagesForChannelQueryKey(channelId: Maybe<string>, search: Maybe<string>) {
  const key = [QueryCacheEnum.CHANNELS, channelId, QueryCacheEnum.MESSAGES]
  if (search && search.length >= MIN_SEARCH_LENGTH) {
    return key.concat([QueryCacheEnum.SEARCH, search.toLowerCase()])
  }

  return key
}

export function messagesForChannelQueryFn({
  queryKey,
  pageParam,
  signal,
}: QueryFunctionContext<ReturnType<typeof messagesForChannelQueryKey>>) {
  const channel = queryKey[1]
  const search = queryKey.length === 5 ? queryKey[4] : null
  return fetchMessagesForChannelUsingGET({ channelId: channel!, cursorHref: pageParam, search, signal })
}

export function useMessagesForChannel(channelId: Maybe<string>, search?: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useInfiniteQuery<
    MessagesForChannelPageResult,
    Error,
    MessagesForChannelPageResult,
    ReturnType<typeof messagesForChannelQueryKey>
  >(messagesForChannelQueryKey(channelId, search), messagesForChannelQueryFn, {
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!channelId && enabled,
    cacheTime: search ? 0 : LONG_CACHE_TIME,
    staleTime: search ? ms('5m') : LONG_STALE_TIME,
  })
}

export function useAddPeopleToChannelMutation() {
  const invalidateChannels = useInvalidateChannelsQueries()
  const bobId = useMyIdentityId()

  return useMutation<void, unknown, { channel: string; members: string[] }>(
    async ({ channel, members }) => {
      return await addMembersToChannelUsingPOST(channel, members)
    },
    {
      async onSuccess(data, { channel, members }) {
        const isBobAdded = Boolean(members.find((item) => item === bobId))
        await invalidateChannels({ channel, singleOnly: !isBobAdded })
      },
    }
  )
}

export function useAddElizaToChannelMutation() {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, string>(
    async (channel: string) => {
      return await addMembersToChannelUsingPOST(channel, [CHATBOT_IDENTITY_ID])
    },
    {
      async onSuccess(data, channel) {
        await queryClient.invalidateQueries(channelQueryKey(channel))
      },
    }
  )
}
